/**
 * Strategy pattern for different checksum algorithms (Single Responsibility)
 */
export class ChecksumCalculator {
  /**
   * Calculate HKID checksum
   */
  static calculateHkidChecksum(id) {
    const weights = [8, 7, 6, 5, 4, 3, 2];
    let sum = 0;

    // Handle first character(s)
    if (id.length === 8) {
      // Single letter
      sum += (id.charCodeAt(0) - 55) * 9;
      for (let i = 1; i < 7; i++) {
        sum += parseInt(id[i]) * weights[i - 1];
      }
    } else {
      // Two letters
      sum += (id.charCodeAt(0) - 55) * 10;
      sum += (id.charCodeAt(1) - 55) * 9;
      for (let i = 2; i < 8; i++) {
        sum += parseInt(id[i]) * weights[i - 2];
      }
    }

    const remainder = sum % 11;
    return remainder === 0 ? '0' : remainder === 1 ? 'A' : (11 - remainder).toString();
  }

  /**
   * Calculate TWID checksum
   */
  static calculateTwidChecksum(id) {
    const letterValues = {
      'A': 10, 'B': 11, 'C': 12, 'D': 13, 'E': 14, 'F': 15, 'G': 16, 'H': 17,
      'I': 34, 'J': 18, 'K': 19, 'L': 20, 'M': 21, 'N': 22, 'O': 35, 'P': 23,
      'Q': 24, 'R': 25, 'S': 26, 'T': 27, 'U': 28, 'V': 29, 'W': 32, 'X': 30,
      'Y': 31, 'Z': 33
    };

    const letterValue = letterValues[id[0].toUpperCase()];
    let sum = Math.floor(letterValue / 10) + (letterValue % 10) * 9;

    for (let i = 1; i < 9; i++) {
      sum += parseInt(id[i]) * (9 - i);
    }

    return (10 - (sum % 10)) % 10;
  }

  /**
   * Verhoeff algorithm for Aadhaar
   */
  static calculateVerhoeffChecksum(digits) {
    const d = [
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
      [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
      [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
      [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
      [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
      [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
      [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
      [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
      [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]
    ];

    const p = [
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
      [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
      [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
      [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
      [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
      [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
      [7, 0, 4, 6, 9, 1, 3, 2, 5, 8]
    ];

    let c = 0;
    const reversedDigits = digits.split('').reverse();

    for (let i = 0; i < reversedDigits.length; i++) {
      c = d[c][p[((i + 1) % 8)][parseInt(reversedDigits[i])]];
    }

    return c;
  }

  /**
   * Calculate NRIC/FIN checksum
   */
  static calculateNricChecksum(id) {
    const weights = [2, 7, 6, 5, 4, 3, 2];
    const stLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'Z', 'J'];
    const fgLetters = ['K', 'L', 'M', 'N', 'P', 'Q', 'R', 'T', 'U', 'W', 'X'];

    let sum = 0;
    for (let i = 1; i < 8; i++) {
      sum += parseInt(id[i]) * weights[i - 1];
    }

    const remainder = sum % 11;
    const firstChar = id[0].toUpperCase();

    if (firstChar === 'S' || firstChar === 'T') {
      return stLetters[remainder];
    } else {
      return fgLetters[remainder];
    }
  }

  /**
   * Calculate Luhn checksum (for SSN, SIN, etc.)
   */
  static calculateLuhnChecksum(digits) {
    let sum = 0;
    let alternate = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let n = parseInt(digits[i]);
      
      if (alternate) {
        n *= 2;
        if (n > 9) {
          n = (n % 10) + 1;
        }
      }
      
      sum += n;
      alternate = !alternate;
    }

    return (10 - (sum % 10)) % 10;
  }

  /**
   * Calculate modulus 11 checksum
   */
  static calculateMod11Checksum(digits, weights = null) {
    if (!weights) {
      weights = Array.from({ length: digits.length }, (_, i) => (i % 6) + 2);
    }

    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      sum += parseInt(digits[i]) * weights[i];
    }

    const remainder = sum % 11;
    return remainder < 2 ? remainder : 11 - remainder;
  }

  /**
   * Calculate BSN (Dutch) checksum
   */
  static calculateBsnChecksum(digits) {
    const weights = [9, 8, 7, 6, 5, 4, 3, 2, -1];
    let sum = 0;

    for (let i = 0; i < 9; i++) {
      sum += parseInt(digits[i]) * weights[i];
    }

    return sum % 11 === 0;
  }

  /**
   * Calculate CPF checksum
   */
  static calculateCpfChecksum(digits) {
    // First check digit
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(digits[i]) * (10 - i);
    }
    let remainder = sum % 11;
    const firstCheck = remainder < 2 ? 0 : 11 - remainder;

    // Second check digit
    sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(digits[i]) * (11 - i);
    }
    sum += firstCheck * 2;
    remainder = sum % 11;
    const secondCheck = remainder < 2 ? 0 : 11 - remainder;

    return [firstCheck, secondCheck];
  }

  /**
   * Calculate RUT checksum
   */
  static calculateRutChecksum(digits) {
    const weights = [2, 3, 4, 5, 6, 7];
    let sum = 0;
    let weightIndex = 0;

    for (let i = digits.length - 1; i >= 0; i--) {
      sum += parseInt(digits[i]) * weights[weightIndex];
      weightIndex = (weightIndex + 1) % weights.length;
    }

    const remainder = sum % 11;
    if (remainder === 0) return '0';
    if (remainder === 1) return 'K';
    return (11 - remainder).toString();
  }

  /**
   * Calculate CUIL checksum
   */
  static calculateCuilChecksum(digits) {
    const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;

    for (let i = 0; i < 10; i++) {
      sum += parseInt(digits[i]) * weights[i];
    }

    const remainder = sum % 11;
    return remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder;
  }
}